import {
  DEFAULT_CHECKOUT_TRUCK_CAPACITY,
  type ResolvedTruckCapacity,
} from "./truck-capacity";

export const MAX_TRUCKLOADS_PER_QUOTE = 10;

export interface CheckoutMinimumFields {
  minOrderQuantity?: number | null;
  minCheckoutQuantity?: number | null;
}

export function getCheckoutMinimumQuantity(
  product: CheckoutMinimumFields,
): number {
  return Math.max(
    1,
    product.minOrderQuantity ?? 1,
    product.minCheckoutQuantity ?? 1,
  );
}

export function isCheckoutQuantityAllowed(
  quantity: number,
  minimum: number,
): boolean {
  return Number.isSafeInteger(quantity) && quantity >= minimum;
}

export function canScheduleTruckloads(
  minimum: number,
  capacity: number | ResolvedTruckCapacity[] | undefined,
  quantity: number,
): boolean {
  const resolvedCapacity =
    typeof capacity === "number"
      ? capacity
      : capacity
        ? Math.max(0, ...capacity.map((option) => option.capacity))
        : DEFAULT_CHECKOUT_TRUCK_CAPACITY;
  if (
    !Number.isSafeInteger(quantity) ||
    !Number.isSafeInteger(minimum) ||
    !Number.isSafeInteger(resolvedCapacity) ||
    quantity < minimum ||
    minimum < 1 ||
    resolvedCapacity < 1
  )
    return false;
  const count = Math.ceil(quantity / resolvedCapacity);
  return count <= MAX_TRUCKLOADS_PER_QUOTE;
}

export function getRecommendedTruckloadQuantity(
  minimum: number,
  capacity: number,
): number {
  return Math.ceil(minimum / capacity) * capacity;
}

export interface PlannedTruckload {
  quantity: number;
  truckType: string;
  truckCapacity: number;
}

export function splitCheckoutTruckloads(
  quantity: number,
  options: ResolvedTruckCapacity[],
): PlannedTruckload[] {
  const truck =
    options.find(
      (option) =>
        Math.ceil(quantity / option.capacity) <= MAX_TRUCKLOADS_PER_QUOTE,
    ) ?? options[0];
  if (!truck || !Number.isSafeInteger(quantity) || quantity < 1) return [];
  return Array.from(
    { length: Math.ceil(quantity / truck.capacity) },
    (_, index) => ({
      quantity: Math.min(truck.capacity, quantity - index * truck.capacity),
      truckType: truck.truckType,
      truckCapacity: truck.capacity,
    }),
  );
}

export function planCheckoutTruckloads(
  quantity: number,
  options: ResolvedTruckCapacity[],
): PlannedTruckload[] {
  return splitCheckoutTruckloads(quantity, options).slice(
    0,
    MAX_TRUCKLOADS_PER_QUOTE + 1,
  );
}
